import axios from 'smg/axios';
import { Start, Stop } from 'smg/buttons';
import * as models from 'smg/customer-api/models';
import { CreateOrderPayments, OrderPaymentsCreateInit } from 'smg/customer-api/orderpayments';
import { NewOrder } from 'smg/customer-api/orders';
import { UpdatePaymentOptionLabel } from 'smg/customer-api/paymentoptions';
import * as paypal from 'smg/customer-api/paypal';
import { IsAxiosError, IsParamErrors, ShowError } from 'smg/errors';
import * as purchaseTracking from 'smg/purchasetracking';
import * as toast from 'smg/toast';

import * as carts from '../carts';
import { Relref } from '../hugo';

import { PendingPaymentOptionID, PersistPendingPaymentOption } from './payment/options';
import { ChargedPayment, SplitSettled, SubmitRunning } from './payment/plan';
import { ApplyPaid, BeginSplitCharge, ChargeOptionID, DeclineReason, EntryScope, FailEntry, MarkEntryCharging, PrepareEntries, ReopenSplit, SettleNewCard, StopSplitRun } from './payment/split';
import { ConfirmSmartPaySubmit } from './smartpay';
import { State } from './state';
import { Payment } from './types';

// SetupSubmit wires the final "Authorize Payment" / "Place Order" button.
// submitOrder is idempotent per click — a flag guards against re-entry
// while the create-order and create-order-payment requests are in flight.
export function SetupSubmit(state: State): void {
    state.submitOrder = () => submitOrder(state);
    state.dom.$authorizePaymentBtn.addEventListener('click', () => submitOrder(state));
}

async function submitOrder(state: State): Promise<void> {
    if (SubmitRunning(state)) {
        return;
    }
    if (state.split) {
        await submitSplit(state);
        return;
    }

    const isPayPal = state.payment.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;

    // New cards stage BlockChyp via CreatePaymentOption without a server id
    // until after the order exists (#8111). Accept either a saved option (id)
    // or a pending create from the payment-confirm step.
    if (!state.shippingOption?.id) {
        return;
    }
    if (!isPayPal && !state.payment.paymentOption?.id && !state.payment.pendingPaymentOptionCreate) {
        return;
    }

    // GSA SmartPay cards paying sales tax confirm before anything starts
    // (#3777) — same submit gate as the other checkouts; the summary banner
    // alone is ignorable. Runs before the page starts the order, so a
    // decline leaves the page untouched.
    if (!await ConfirmSmartPaySubmit(state)) {
        return;
    }

    state.startLoading();
    state.phase = 'charging';
    state.dom.$authorizePaymentBtn.disabled = true;
    document.querySelectorAll('.accordion-header a[data-bs-toggle="collapse"]').forEach($btn => {
        ($btn as HTMLAnchorElement).classList.add('d-none');
    });

    const orderFunc = state.orderResp ?
        Promise.resolve(state.orderResp) :
        createOrder(state);

    const createOrderPayments = isPayPal ? undefined : buildCreateOrderPayments(state);

    await orderFunc.
        then(createOrderPayments).
        then(async () => {
            await markAccountDefault(state);
            await completeCheckout(state, isPayPal);
        }).
        catch((err: unknown) => {
            // The card was charged but finishing failed (completeCheckout showed why). Give the customer the
            // button back; the retry skips the charge and only finishes.
            if (state.payment.success) {
                state.stopLoading();
                state.dom.$authorizePaymentBtn.disabled = false;
            }
            throw err;
        }).
        finally(() => {
            state.phase = 'editing';
        });
}

async function submitSplit(state: State): Promise<void> {
    if (!state.shippingOption?.id) {
        return;
    }
    // Every card is paid (finishing failed earlier); Retry only finishes the order.
    if (state.split && state.orderResp && SplitSettled(state.split)) {
        state.phase = 'finishing';
        Start(state.dom.$authorizePaymentBtn);
        try {
            await markAccountDefault(state);
            await completeCheckout(state, false);
        } catch {
            // completeCheckout already showed why; the button stays for another try.
        } finally {
            state.phase = 'editing';
            Stop(state.dom.$authorizePaymentBtn);
        }
        return;
    }
    // Preparing tokenizes cards over the network; a second click in that window must not start another run.
    state.phase = 'preparing';
    let prepared: boolean;
    let confirmed = false;
    try {
        prepared = await PrepareEntries(state);
        confirmed = prepared && await ConfirmSmartPaySubmit(state);
    } finally {
        if (!confirmed) {
            state.phase = 'editing';
        }
    }
    if (!prepared) {
        state.paymentStepComplete = false;
        ReopenSplit(state);
        return;
    }
    if (!confirmed) {
        return;
    }

    state.dom.$authorizePaymentBtn.disabled = true;
    Start(state.dom.$authorizePaymentBtn);
    state.phase = 'charging';
    BeginSplitCharge(state);
    let failed = false;
    try {
        if (!state.orderResp) {
            await createOrder(state);
        }
        if (!state.orderResp || !state.split) {
            throw new Error('Order not found');
        }
        const orderID = state.orderResp.orderID;
        let n = 0;
        for (const entry of state.split.entries) {
            if (entry.status === 'paid') {
                continue;
            }
            n += 1;
            MarkEntryCharging(state, entry, n);
            const createdNow = Boolean(entry.payment.pendingPaymentOptionCreate);
            try {
                if (!entry.sameAs) {
                    await PersistPendingPaymentOption(state, orderID, EntryScope(entry));
                    if (createdNow) {
                        SettleNewCard(entry);
                    }
                    noteCreatedPaymentOption(state, entry.payment, createdNow);
                }
            } catch (err) {
                failed = true;
                state.phase = 'recovering';
                StopSplitRun(state);
                toast.Error('Payment failed.');
                ShowError(err);
                ReopenSplit(state);
                return;
            }
            entry.idempotencyKey ??= crypto.randomUUID();
            try {
                const resp = await CreateOrderPayments({
                    'user': await state.getUser(),
                    'orderIDs': [orderID],
                    'paymentOptionID': ChargeOptionID(state.split.entries, entry),
                    'amount': Number(entry.amount.toFixed(2)),
                    'idempotencyKey': entry.idempotencyKey,
                });
                const created = resp.orderPayments[0];
                if (!created) {
                    throw new Error('Payment failed');
                }
                ApplyPaid(entry, created);
                state.updatePaymentSummary();
                state.syncSplitSummary();
            } catch (err) {
                failed = true;
                state.phase = 'recovering';
                const reason = DeclineReason(err);
                if (reason !== undefined) {
                    FailEntry(state, entry, 'declined', reason);
                    toast.Error('Payment failed.');
                    ShowError(reason);
                } else {
                    FailEntry(state, entry, 'unknown', '');
                    toast.Error('Payment failed.');
                    ShowError(err);
                }
                ReopenSplit(state);
                return;
            }
        }
        await markAccountDefault(state);
        await completeCheckout(state, false);
    } catch {
        failed = true;
        state.phase = 'recovering';
        StopSplitRun(state);
        ReopenSplit(state);
    } finally {
        state.phase = 'editing';
        Stop(state.dom.$authorizePaymentBtn);
        if (failed) {
            state.syncSplitSummary();
        }
    }
}

async function createOrder(state: State): Promise<models.OrderCreateResponse | undefined> {
    return NewOrder({
        user: await state.getUser(),
        cartID: state.cartID ?? (await carts.GetCart())?.cart?.id ?? '',
        shippingOptionIDs: [state.shippingOption?.id],
        userTaxExemptionID: state.taxExemption?.id,
        eventDate: state.additionalDetails.eventDate?.toISODate() ?? undefined,
        sms: state.additionalDetails.smsOptIn,
        promotionalApproval: state.additionalDetails.marketingPermission,
        promo: state.promo?.code,
    }).then(orderResp => {
        state.orderResp = orderResp;
        purchaseTracking.TrackPurchase({
            invoiceNumber: orderResp.invoiceNumber.toString(),
            orderID: orderResp.orderID,
            value: parseFloat(state.totalAmount.toString()),
            cartItems: state.cartItems,
        });
        return orderResp;
    }).catch(e => {
        ShowError(e);
        state.dom.$authorizePaymentBtn.disabled = false;
        state.stopLoading();
        throw e;
    });
}

// buildCreateOrderPayments returns a thunk that posts /order-payments for
// the single payment, guarded by its own in-flight flag so rapid
// double-clicks don't cause duplicate charges.
function buildCreateOrderPayments(state: State): () => Promise<void> {
    let inFlight = false;
    return async () => {
        if (inFlight) {
            return;
        }
        inFlight = true;

        if (!state.orderResp) {
            // Throw (not return) so the outer .then(completeCheckout) is
            // skipped — otherwise we'd navigate to order-success with no
            // payment recorded.
            ShowError('Order not found');
            throw new Error('Order not found');
        }
        const p = state.payment;
        // A charge that already went through is never sent again; a retry after a failed finish only finishes.
        if (p.success) {
            inFlight = false;
            return;
        }
        const createdNow = Boolean(p.pendingPaymentOptionCreate);

        // Mint the staged BlockChyp token under the order brand now that the
        // order exists (#8111). Saved options already have an id and skip this.
        try {
            await PersistPendingPaymentOption(state, state.orderResp.orderID);
            noteCreatedPaymentOption(state, p, createdNow);
        } catch (err) {
            inFlight = false;
            handleOrderPaymentError(state, p, err);
        }

        state.orderPaymentIdemKey ??= crypto.randomUUID();
        const req: OrderPaymentsCreateInit = {
            'user': await state.getUser(),
            'orderIDs': [state.orderResp.orderID],
            'paymentOptionID': p.paymentOption?.key ?? p.paymentOption?.id,
            'amount': state.totalAmount.toNumber(),
            'idempotencyKey': state.orderPaymentIdemKey,
        };
        if (p.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
            req['poNumber'] = p.purchaseOrder?.purchaseOrderNumber;
            req['poUploadIDs'] = p.purchaseOrder?.purchaseOrderUploadIDs;
        }
        if (!state.sess?.user.id) {
            req['user'] = await state.getUser();
        }

        await CreateOrderPayments(req).
            then(() => {
                state.orderPaymentIdemKey = undefined;
                p.success = true;
                p.msg = 'Payment successful';
            }).
            catch(err => handleOrderPaymentError(state, p, err)).
            finally(() => {
                inFlight = false;
            });
    };
}

function noteCreatedPaymentOption(state: State, payment: Payment, createdNow: boolean): void {
    const id = payment.paymentOption?.id;
    if (!createdNow || !id || id === PendingPaymentOptionID) {
        return;
    }
    state.createdThisCheckout.add(id);
}

// markAccountDefault writes the account's default label once, after every charge has succeeded.
// It is best effort and bounded: a stalled write waits at most five seconds, then the confirmation
// runs anyway. In a split the last switched card in charge order keeps the label. A run that stops
// on a decline or an unconfirmed charge writes nothing until a later retry completes the order.
async function markAccountDefault(state: State): Promise<void> {
    if (!state.sess?.user.id) {
        return;
    }
    const id = defaultCardToLabel(state);
    if (!id) {
        return;
    }
    await Promise.race([
        UpdatePaymentOptionLabel(id, 'Default').catch(err => console.error(err)),
        new Promise(resolve => setTimeout(resolve, 5000)),
    ]);
}

function defaultCardToLabel(state: State): string | undefined {
    const split = state.split;
    if (split) {
        let id: string | undefined;
        for (const entry of split.entries) {
            const optionID = ChargedPayment(split.entries, entry).paymentOption?.id;
            if (entry.payment.setDefault && optionID && state.createdThisCheckout.has(optionID)) {
                id = optionID;
            }
        }
        return id;
    }
    const optionID = state.payment.paymentOption?.id;
    if (state.payment.setDefault && optionID && state.createdThisCheckout.has(optionID)) {
        return optionID;
    }
    return undefined;
}

function handleOrderPaymentError(state: State, p: Payment, err: unknown): never {
    // submitOrder() hides every accordion edit link and starts the global
    // loading spinner. On payment failure we stay on the page, so restore all
    // of that — not just the payment section — otherwise the page is stuck on
    // a full-screen spinner with no way to edit the other sections.
    state.stopLoading();
    document.querySelectorAll('.accordion-header a[data-bs-toggle="collapse"]').forEach($btn => {
        ($btn as HTMLAnchorElement).classList.remove('d-none');
    });
    // The order already exists (a resumed order, or the failed charge's own
    // create) — shipping/tax edits can't apply to it, so keep those toggles
    // locked instead of restoring them with the payment edit link above.
    if (state.orderResp) {
        state.dom.$shippingBodyToggle?.classList.add('d-none');
        if (!state.taxStepComplete) {
            state.dom.$taxBodyToggle?.classList.add('d-none');
        }
    }

    const $paymentSectionBtn = state.dom.$sectionPayment?.querySelector('.accordion-header a[data-bs-toggle="collapse"]') as HTMLAnchorElement;
    state.dom.$authorizePaymentBtn.disabled = false;
    $paymentSectionBtn.click();

    toast.Error('Payment failed.');
    // The card label takes the retry text; replacing the button's whole text would drop its other labels, among
    // them the split's "Retry {amount}".
    const { dom } = state;
    [dom.$authorizePaymentTextPlaceOrder, dom.$authorizePaymentTextPayPal, document.querySelector('#authorize-payment-text-split')].
        forEach($ => $?.classList.add('d-none'));
    dom.$authorizePaymentText.textContent = 'Retry Payment';
    dom.$authorizePaymentText.classList.remove('d-none');

    p.success = false;
    p.msg = 'Payment failed';
    // An answered decline frees the key (customer-api releases it too), so a held key means a charge with no answer.
    if (DeclineReason(err) !== undefined) {
        state.orderPaymentIdemKey = undefined;
    }
    if (IsAxiosError(err) && IsParamErrors(err.response?.data)) {
        const errors = err.response?.data.errors;
        if (errors?.length) {
            p.msg = errors[0].message;
        }
    }
    ShowError(p.msg);
    throw new Error(p.msg);
}

async function completeCheckout(state: State, isPayPal: boolean): Promise<void> {
    if (isPayPal) {
        const url = await paypal.CreateOrder({
            user: await state.getUser(),
            orderIDs: [state.orderResp?.orderID ?? ''],
            successURL: `${window.location.protocol}//${window.location.host}${Relref('/order-success')}?id=${state.orderResp?.key ?? ''}&deleteCart=true`,
            cancelURL: `${window.location.protocol}//${window.location.host}${Relref('/checkout')}`,
        });
        window.location.href = url || '';
        return;
    }
    state.phase = 'finishing';
    state.cartReloader?.abort();
    try {
        // Every charge succeeded. A retry after a failed finish finds the cart already gone and only finishes.
        if (carts.GetCartID()) {
            await carts.DeleteCart();
        }
        await axios.post(`/orders/${state.orderResp?.orderID ?? ''}/checkout-complete`);
    } catch (err) {
        ShowError(err);
        throw err;
    }
    window.location.href = `${Relref('/order-success')}?id=${state.orderResp?.key ?? ''}`;
}
